import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Activity, ActivityInstance } from '@api';
import { ActivityApi } from '@/api/apis/ActivityApi';
import { SupervisorContext } from '@/contexts/SupervisorContext';
import { useStarValuePersona } from '@/personas/useStarValuePersona';

/**
 * Aktiviteter vars stjärnvärde väntar på förälderns besked.
 *
 * EN hook, två läsare med samma underliggande fråga:
 *   - Handledarläge → förälderns kö för vald klient (`activities`, räknaren i hubben).
 *   - Brukarens egen vy → hens egna väntande förslag (`isPending`, markeringen på
 *     aktivitetskorten i dags- och veckovyn). Gäller BÅDE barn och tonåring: sedan V004
 *     begär båda värdet av sin handledare, och `needsApproval` i useStarValuePersona
 *     säger exakt det.
 *
 * Frågan ställs BARA i de lägena. Vuxenvyn har inga stjärnor — där vore anropet ren
 * rundgång, och hooken svarar med en tom kö utan att röra nätverket (den ligger i
 * dags-/veckovyer som alla personas renderar).
 *
 * `isPending` matchar på instansens `parentActivityId`: statusen sitter på AKTIVITETEN,
 * så varje instans av en återkommande aktivitet väntar på samma besked.
 *
 * Fel sväljs i API-lagret (undefined) och senast kända kö behålls i stället för att
 * nollas — en markering som blinkar bort vid ett tillfälligt nätverksfel ljuger värre
 * än en som är någon sekund gammal. Läses om när fliken blir synlig igen: föräldern
 * godkänner ofta medan tonåringen har appen uppe, och då ska markeringen försvinna.
 */
export type PendingStarValues = {
  /** Väntande aktiviteter, nyast först. Tom när frågan inte är tillämplig. */
  activities: Activity[];
  /** True tills första svaret landat. Håller "inget väntar"-texten borta innan dess. */
  loading: boolean;
  /** True när instansens aktivitet väntar på besked. */
  isPending: (instance: ActivityInstance) => boolean;
  reload: () => void;
};

export const usePendingStarValues = (): PendingStarValues => {
  const { currentClient, isSupervising } = useContext(SupervisorContext);
  const { needsApproval } = useStarValuePersona();

  // Handledaren läser klientens kö; tonåringen sin egen (ingen tag).
  const supervisedTag = isSupervising ? currentClient?.tag : undefined;
  const shouldLoad = isSupervising ? currentClient != null : needsApproval;

  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState<boolean>(shouldLoad);
  const mounted = useRef<boolean>(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback(async () => {
    if (!shouldLoad) {
      setActivities([]);
      setLoading(false);
      return;
    }
    const result = await new ActivityApi().getStarValueProposals({ supervisedTag });
    if (!mounted.current) return;
    setLoading(false);
    if (result === undefined) return;
    setActivities(result);
  }, [shouldLoad, supervisedTag]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') reload();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [reload]);

  const pendingIds = useMemo(
    () => new Set(activities.map((activity) => activity.activityId)),
    [activities],
  );

  const isPending = useCallback(
    (instance: ActivityInstance) => pendingIds.has(instance.parentActivityId),
    [pendingIds],
  );

  return { activities, loading, isPending, reload };
};
